import { useState } from 'react'
import { toast } from '../../lib/toast'
import styles from './GuideSelect.module.css'
import step1 from './images/step1.png';
import step4 from './images/step4.png';
import step3 from './images/step3.png';
import step5 from './images/step5.png';
import step6 from './images/step6.png';
import { Copy } from 'lucide-react';
import ImageModal from '../../ui/imageModal/ImageModal';

export default function ConnectClaude() {
  const [mcpServerUrl] = useState(`${import.meta.env.VITE_API_OAUTH_BASE_URL}/mcp`)
  const [modalImg, setModalImg] = useState<string|null>(null);


  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toast.success(`${label} copied to clipboard!`)
  }

  return (
    <div className={styles.container}>

      <div className={styles.header}>
        <h1>Connect Claude to MCP</h1>
        <p>Follow these visual steps to connect your tools to Claude</p>
      </div>

      <div className={styles.steps}>
        {/* Step 1: Open Claude Settings & Go to Connectors */}
        <div className={styles.step}>
          <div className={styles.stepNumber}>1</div>
          <div className={styles.stepContent}>
            <h2>Open Claude Settings and Go to Connectors</h2>
            <p>
              Open Claude and click on <strong>Settings</strong> in the bottom left corner.<br />
              In the Settings menu, find and click on <strong>Connectors</strong>.
            </p>
            <div className={styles.imageContainer}>
              <img
                src={step1}
                alt="Open Claude Settings"
                className={styles.stepImage}
                style={{ cursor: 'zoom-in' }}
                onClick={() => setModalImg(step1)}
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 1: Open Settings%3C/text%3E%3C/svg%3E'
                }}
              />
            </div>
   
          </div>
        </div>

        {/* Step 4: Configure Connector */}
        <div className={styles.step}>
          <div className={styles.stepNumber}>2</div>
          <div className={styles.stepContent}>
            <h2>Configure Your MCP Connector</h2>
            <p>Click on <strong>Add Custom Connector</strong></p>
            <p>Set the connector name to <code>mcp</code> and paste your server URL</p>

            <div className={styles.configForm}>
              <div className={styles.formGroup}>
                <label>Connector Name:</label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    value={'MCP Studio'}
                    disabled={true}
                    className={styles.input}
                  />
                  <button
                    onClick={() => copyToClipboard('MCP Studio', 'Connector Name')}
                    className={styles.copyBtn}
                  ><Copy size={16} /></button>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>MCP Server URL:</label>
                <div className={styles.inputGroup}>
                  <input
                    type="text"
                    value={mcpServerUrl || 'https://your-mcp-server.com/mcp'}
                    className={styles.input}
                    disabled={true}
                  />
                  <button
                    onClick={() => copyToClipboard(mcpServerUrl || 'https://your-mcp-server.com/mcp', 'MCP Server URL')}
                    className={styles.copyBtn}
                  ><Copy size={16} /></button>
                </div>
              </div>
            </div>

            <div className={styles.imageContainer}>
              <img
                src={step3}
                alt="Configure MCP Connector"
                className={styles.stepImage}
                onClick={() => setModalImg(step3)}
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 4: Configure Connector%3C/text%3E%3C/svg%3E'
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.step}>
          <div className={styles.stepNumber}>3</div>
          <div className={styles.stepContent}>
            <h2>Login and authorize</h2>
            <p>Click on <strong>Connect</strong>.</p>
            <p>You will be prompted to log in and authorize with your MCP username and password.<br />
              Use the same credentials you use to log in to this system.
            </p>
          </div>
        </div>

        <div className={styles.step}>
          <div className={styles.stepNumber}>4</div>
          <div className={styles.stepContent}>
            <h2>Open Tools in Claude</h2>
            <p>Open new chat and click on the <strong>tools</strong> icon to see your available MCP connectors and tools</p>
            <div className={styles.imageContainer}>
              <img
                src={step4}
                alt="Open Tools Section"
                className={styles.stepImage}
                onClick={() => setModalImg(step4)}
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 5: View Tools%3C/text%3E%3C/svg%3E'
                }}
              />
            </div>
          </div>
        </div>

        {/* Step 6: View Available Tools */}
        <div className={styles.step}>
          <div className={styles.stepNumber}>5</div>
          <div className={styles.stepContent}>
            <h2>See Your Available Tools</h2>
            <p>You should see your MCP tools listed and ready to use</p>
            <div className={styles.imageContainer}>
              <img
                src={step5}
                alt="Available Tools List"
                className={styles.stepImage}
                onClick={() => setModalImg(step5)}
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 6: Available Tools%3C/text%3E%3C/svg%3E'
                }}
              />
            </div>
          </div>
        </div>

        {/* Step 7: Try Using Tools */}
        <div className={styles.step}>
          <div className={styles.stepNumber}>6</div>
          <div className={styles.stepContent}>
            <h2>Try Using a Tool</h2>
            <p>Start a conversation in Claude and try using one of your tools</p>
            {/* 
            <div className={styles.examplePrompts}>
              <div className={styles.promptCard}>
                <div className={styles.promptIcon}>💼</div>
                <h4>Create Business Partner</h4>
                <p className={styles.promptText}>
                  "Create a new business partner for Acme Robotics Ltd. located at Main St 10, Tel Aviv, Israel with email ops@acme.com"
                </p>
              </div>
              <div className={styles.promptCard}>
                <div className={styles.promptIcon}>🔍</div>
                <h4>Search Materials</h4>
                <p className={styles.promptText}>
                  "Search for materials that start with 'MAT' in plant 1000"
                </p>
              </div>
            </div> */}

            <div className={styles.imageContainer}>
              <img
                src={step6}
                alt="Using MCP Tools"
                className={styles.stepImage}
                onClick={() => setModalImg(step6)}
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect width="800" height="400" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="%23666"%3EStep 7: Try a Tool%3C/text%3E%3C/svg%3E'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.successCard}>
        <h2>🎉 You're All Set!</h2>
        <p>Claude can now use your tools through the MCP connector. Start chatting and let Claude help you with operations!</p>
      </div>

      <div className={styles.troubleshooting}>
        <h2>Need Help?</h2>
        <div className={styles.troubleItem}>
          <h3>❌ Claude doesn't see my tools</h3>
          <ul>
            <li>Verify the MCP server URL is correct and accessible</li>
            <li>Ensure your tools are enabled in the Tools section of this app</li>
            <li>Restart Claude completely</li>
          </ul>
        </div>
        <div className={styles.troubleItem}>
          <h3>⚠️ Tool execution fails</h3>
          <ul>
            <li>Test your connection in the Connections section</li>
            <li>Verify your credentials are correct</li>
            <li>Check that CSRF tokens are properly configured</li>
          </ul>
        </div>
      </div>
         {/* Modal for large image view */}
      {modalImg && (
        <ImageModal src={modalImg} alt="Step" onClose={() => setModalImg(null)} />
      )}
    </div>
  )
}